
import { PendingCriterionData } from '@/jbi-shared/types/criterions.types';
import { FullDocumentRevisionObject } from '@/jbi-shared/types/full-document-revision-object.types';
import { validateExistingCriterionLinkedBpr } from '@/jbi-shared/util/cplus-criterion.util';
import { opsToText } from '@/utils/quill-delta.util';
import { ViewModeMixin } from '@/utils/viewMode.mixin';
import { mixins } from 'vue-class-component';
import { Component, Prop, Watch } from 'vue-property-decorator';
import StagingCriterionFormEditModal from './StagingCriterionFormEditModal.vue';
import { DirtyTagMap } from '@/store/modules/documents/types/documents.types';
import { deepEqual } from '@/jbi-shared/util/deep-copy.util';

@Component({})
export default class StagingCriterionFormStepOne extends mixins(ViewModeMixin) {
  @Prop()
  public existingCriterions!: FullDocumentRevisionObject['revision']['sections']['criterionSection'];
  @Prop()
  public validCriterions!: PendingCriterionData[];
  @Prop()
  public newlyAddedCriterions!: PendingCriterionData[];
  @Prop()
  public dirtyCriterions!: PendingCriterionData[];
  @Prop()
  public dirtyBprs!: FullDocumentRevisionObject['revision']['sections']['bprSection'];
  @Prop()
  public invalidCriterions!: PendingCriterionData[];
  @Prop()
  public selectedCriterions!: PendingCriterionData[];
  @Prop()
  public dirtyTagMaps!: DirtyTagMap[];

  get proxySelectedCriterions() {
    return this.selectedCriterions;
  }
  set proxySelectedCriterions(value) {
    this.$emit('update:selectedCriterions', value);
  }

  public getCriterionIndex(criterion: PendingCriterionData): number {
    if (this.isRemovedCriterion(criterion)) {
      return -1;
    }

    return this.selectedCriterions.findIndex((selectedCriterion) =>
      this.criteriaMatch(selectedCriterion, criterion)
    );
  }

  public formatCriterion(criterion: PendingCriterionData) {
    return criterion.content.title;
  }

  public isRemovedCriterion(criterion: PendingCriterionData) {
    const isSelected = this.selectedCriterions.find((selected) => {
      return this.criteriaMatch(selected, criterion);
    });
    return !isSelected;
  }

  public isInvalidCriterion(criterion: PendingCriterionData) {
    const latestVersion = this.dirtyCriterions.find((dirty) =>
      this.criteriaMatch(dirty, criterion)
    );
    if (latestVersion) {
      return !latestVersion.isValid;
    }
    return !criterion.isValid;
  }

  public isMissingLinkedBpr(criterion: PendingCriterionData) {
    return (
      criterion.isValid &&
      !validateExistingCriterionLinkedBpr(
        criterion as FullDocumentRevisionObject['revision']['sections']['criterionSection'][0],
        this.dirtyBprs
      )
    );
  }

  public criteriaMatch(
    criterion1: PendingCriterionData,
    criterion2: PendingCriterionData
  ): boolean {
    // Check if criteria match by comparing their unique identifiers
    // First try to match by tempId (for newly added criteria)
    if (criterion1.tempId && criterion2.tempId) {
      return criterion1.tempId === criterion2.tempId;
    }

    // If no tempId match, use the deepequal function
    return deepEqual(criterion1, criterion2);
  }

  public openAddNewCriteriaModal() {
    this.$buefy.modal.open({
      parent: this,
      component: StagingCriterionFormEditModal,
      hasModalCard: true,
      width: 640,
      trapFocus: true,
      props: {
        modalTitle: 'Add Audit Criteria',
        dirtyTagMaps: this.dirtyTagMaps,
        dirtyBprs: this.dirtyBprs
      },
      events: {
        'update:criterion': this.onCriterionAdded,
        'update:dirtyTagMaps': (e: DirtyTagMap[]) =>
          this.$emit('update:dirtyTagMaps', e),
        close: () => {
          this.$emit('modalClosed');
        }
      }
    });
  }

  public onCriterionAdded(criterion: PendingCriterionData) {
    const updatedCriterions = [...this.dirtyCriterions, criterion];

    this.$emit('update:dirtyCriterions', updatedCriterions);
    this.$emit('criterionAdded', criterion);

    let parentComponent = this.$parent as any;
    while (parentComponent && !parentComponent.dirtyCriterions) {
      parentComponent = parentComponent.$parent as any;
    }

    if (parentComponent && parentComponent.dirtyCriterions) {
      if (parentComponent.$emit) {
        parentComponent.$emit('update:criterions', updatedCriterions);
      } else {
        parentComponent.dirtyCriterions = updatedCriterions;
      }

      parentComponent.$forceUpdate();
      let modalParent = this.$parent as any;
      while (modalParent && !modalParent.dirtyCriterions) {
        modalParent = modalParent.$parent as any;
      }
      if (modalParent) {
        modalParent.$forceUpdate();
      }
    }
  }

  @Watch('dirtyCriterions', { deep: true, immediate: true })
  public onDirtyCriterionsUpdated(newCriterions: PendingCriterionData[]) {
    this.$forceUpdate();
  }

  @Watch('newlyAddedCriterions', { deep: true, immediate: true })
  public onNewlyAddedCriterionsUpdated(newCriterions: PendingCriterionData[]) {
    this.$forceUpdate();
  }
}
